import type { TeamMemberView } from '~/modules/professional/type';
import type { MessageTooltipContentProps } from '@archipro-design/aria';
import {
    Image,
    Label,
    useAppDisplayModeContext,
    useStyles,
    Flex,
    MessageTooltip,
} from '@archipro-design/aria';
import * as S from './TeamMemberModalContent.style';
import { useTracker, type TrackingEvent } from '@archipro-website/tracker';
import { Envelope, LinkedinBoxFill, Phone } from '@archipro-design/icons';
import { useState } from 'react';

import { hasRenderableTeamMemberSummary } from './hasRenderableTeamMemberSummary';

interface TeamMemberModalContentProps {
    website: string;
    memberDetails: TeamMemberView;
}

export const MOBILE_IMAGE_DIMENSIONS = { width: 202, height: 260 };
export const DESKTOP_IMAGE_DIMENSIONS = { width: 395, height: 465 };

const TeamMemberModalContent = ({
    website,
    memberDetails,
}: TeamMemberModalContentProps) => {
    const hasSummary = hasRenderableTeamMemberSummary(memberDetails.summary);
    const { css } = useStyles({ hasSummary });
    const { log } = useTracker();
    const { mobile: isMobile } = useAppDisplayModeContext();

    const imageDimensions = isMobile
        ? MOBILE_IMAGE_DIMENSIONS
        : DESKTOP_IMAGE_DIMENSIONS;

    const clickLink = (trackingEvent: TrackingEvent) => {
        log(trackingEvent, {
            url: new URL(window.location.href),
            data: {
                ExternalURL: website,
                Source: 'detail, teamMemberModal',
                ItemType: 'TeamMember',
            },
        });
    };

    const [showNumberToolTip, setShowNumberToolTip] = useState(false);

    const handleDesktopPhoneReveal = () => {
        setShowNumberToolTip(true);
        clickLink('PhoneReveal');
    };

    const phoneToolTip: MessageTooltipContentProps = {
        className: css(S.PhoneTooltip),
        children: (
            <a href={`tel:${memberDetails.phone}`}>{memberDetails.phone}</a>
        ),
        padding: 8,
    };

    const linkedInUrl = memberDetails.linkedInLink?.startsWith('http')
        ? memberDetails.linkedInLink
        : `https://${memberDetails.linkedInLink}`;

    return (
        <div className={css(S.TeamMemberDialogContentBase)}>
            <div className={css(S.ProfileOverview)}>
                <Image
                    src={memberDetails.detailsPageImageSrc}
                    layout={'fill'}
                    width={imageDimensions.width}
                    height={imageDimensions.height}
                    alt={memberDetails?.name ?? 'Team member avatar photo'}
                />
                <Label className={css(S.TeamMemberName)}>
                    {memberDetails.name}
                </Label>
                {!memberDetails.hideJobTitle && (
                    <Label className={css(S.TeamMemberRole)}>
                        {memberDetails.jobTitle}
                    </Label>
                )}
                <Flex
                    className={css(S.TeamMemberContactLinksContainer)}
                    gap="space-12"
                    hAlign="center"
                >
                    {memberDetails.phone &&
                        !memberDetails.hidePhone &&
                        (isMobile ? (
                            <a
                                className={css(S.TeamMemberContactIconButton)}
                                href={`tel:${memberDetails.phone}`}
                                onClick={() => clickLink('PhoneReveal')}
                                data-button={`${memberDetails.id}-team-member-phone-reveal`}
                            >
                                <Phone />
                            </a>
                        ) : (
                            <MessageTooltip
                                open={showNumberToolTip}
                                content={phoneToolTip}
                                trigger={
                                    <div
                                        className={css(
                                            S.TeamMemberContactIconButton,
                                        )}
                                        onClick={handleDesktopPhoneReveal}
                                    >
                                        <Phone />
                                    </div>
                                }
                                position="below"
                                offset={[0, 16]}
                            />
                        ))}
                    {memberDetails.email && !memberDetails.hideEmail && (
                        <a
                            className={css(S.TeamMemberContactIconButton)}
                            href={`mailto:${memberDetails.email}`}
                            onClick={() => clickLink('EmailReveal')}
                        >
                            <Envelope />
                        </a>
                    )}
                    {memberDetails.linkedInLink &&
                        !memberDetails.hideLinkedInLink && (
                            <a
                                className={css(S.TeamMemberContactIconButton)}
                                href={linkedInUrl}
                                onClick={() => clickLink('Website')}
                                target="_blank"
                                rel="noopener"
                            >
                                <LinkedinBoxFill />
                            </a>
                        )}
                </Flex>
            </div>

            {hasSummary && (
                <div
                    className={css(S.TeamMemberDescriptionBlock)}
                    data-testid="team-member-modal-description"
                >
                    <div
                        className={css(S.DescriptionText)}
                        dangerouslySetInnerHTML={{
                            __html: memberDetails.summary ?? '',
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default TeamMemberModalContent;
